interface Step {
  number: number;
  nameEn: string;
  nameEs: string;
  descEn: string;
  descEs: string;
  highlight?: boolean;
}

const STEPS: Step[] = [
  {
    number: 1,
    nameEn: 'Checkout code',
    nameEs: 'Checkout del código',
    descEn: 'Clone the repository at the current commit SHA',
    descEs: 'Clona el repositorio en el commit SHA actual',
  },
  {
    number: 2,
    nameEn: 'Install dependencies',
    nameEs: 'Instalar dependencias',
    descEn: 'npm ci — clean install from lockfile for reproducible builds',
    descEs: 'npm ci — instalación limpia desde lockfile para builds reproducibles',
  },
  {
    number: 3,
    nameEn: 'Lint',
    nameEs: 'Lint',
    descEn: 'ESLint + Prettier checks across all packages',
    descEs: 'ESLint + Prettier en todos los paquetes',
  },
  {
    number: 4,
    nameEn: 'Test backend',
    nameEs: 'Tests del backend',
    descEn: 'Jest unit tests for Lambda handlers and business logic',
    descEs: 'Tests unitarios Jest para handlers Lambda y lógica de negocio',
  },
  {
    number: 5,
    nameEn: 'Test infra (CDK assertions)',
    nameEs: 'Tests de infra (CDK assertions)',
    descEn: 'Validate CloudFormation templates with aws-cdk-lib/assertions',
    descEs: 'Valida templates CloudFormation con aws-cdk-lib/assertions',
  },
  {
    number: 6,
    nameEn: 'Configure AWS credentials',
    nameEs: 'Configurar credenciales AWS',
    descEn: 'OIDC token exchange — no long-lived secrets stored anywhere',
    descEs: 'Intercambio de token OIDC — sin secretos de larga duración',
  },
  {
    number: 7,
    nameEn: 'CDK Deploy (infrastructure)',
    nameEs: 'CDK Deploy (infraestructura)',
    descEn: 'Deploy API Gateway, Lambda, DynamoDB, S3, CloudFront stacks',
    descEs: 'Deploy de API Gateway, Lambda, DynamoDB, S3, CloudFront',
  },
  {
    number: 8,
    nameEn: 'Get stack outputs (API URLs)',
    nameEs: 'Obtener outputs del stack (URLs de API)',
    descEn: 'Read CloudFormation outputs — API endpoint URLs now known',
    descEs: 'Lee outputs de CloudFormation — ahora conocemos las URLs de la API',
    highlight: true,
  },
  {
    number: 9,
    nameEn: 'Build frontend (with API URLs injected)',
    nameEs: 'Build del frontend (con URLs de API inyectadas)',
    descEn: 'Vite build with VITE_API_URL env var set from step 8 output',
    descEs: 'Vite build con VITE_API_URL inyectado desde el output del paso 8',
    highlight: true,
  },
  {
    number: 10,
    nameEn: 'Deploy to S3',
    nameEs: 'Deploy a S3',
    descEn: 'Sync built assets to the origin S3 bucket',
    descEs: 'Sincroniza los assets compilados al bucket S3 de origen',
  },
  {
    number: 11,
    nameEn: 'Invalidate CloudFront cache',
    nameEs: 'Invalidar caché de CloudFront',
    descEn: 'Force CDN edge nodes to serve the new build immediately',
    descEs: 'Fuerza a los nodos edge del CDN a servir el nuevo build de inmediato',
  },
  {
    number: 12,
    nameEn: 'Seed database',
    nameEs: 'Seed de la base de datos',
    descEn: 'Upsert portfolio data into DynamoDB from the seed script',
    descEs: 'Upsert de datos del portfolio en DynamoDB desde el script de seed',
  },
];

interface CicdPipelineProps {
  lang: string;
}

export function CicdPipeline({ lang }: CicdPipelineProps) {
  const isEn = lang === 'en';

  return (
    <div className="relative">
      {/* Key insight callout */}
      <div className="mb-8 rounded border border-accent/30 bg-accent/5 px-4 py-3">
        <p className="font-mono text-xs text-accent">
          {isEn
            ? '// Key insight: frontend build happens AFTER CDK deploy so API URLs can be injected as env vars'
            : '// Insight clave: el build del frontend ocurre DESPUÉS del CDK deploy para poder inyectar las URLs de la API como env vars'}
        </p>
      </div>

      <div className="relative">
        {STEPS.map((step, idx) => {
          const isLast = idx === STEPS.length - 1;
          const isHighlight = step.highlight;
          const isInjectionArrow = idx === 7; // after step 8, before step 9

          return (
            <div key={step.number}>
              {/* Injection arrow annotation between steps 8 and 9 */}
              {isInjectionArrow && (
                <div className="relative ml-5 flex items-center">
                  {/* Connector line continues */}
                  <div className="absolute left-0 top-0 bottom-0 w-px bg-accent/60" />
                  {/* Arrow annotation */}
                  <div className="ml-8 my-0 flex items-center gap-3">
                    <div className="flex flex-col items-center">
                      <div className="h-3 w-px bg-accent/60" />
                      <svg
                        className="text-accent"
                        width="14"
                        height="10"
                        viewBox="0 0 14 10"
                        fill="currentColor"
                      >
                        <path d="M7 10L0 0h14L7 10z" />
                      </svg>
                    </div>
                    <span className="rounded border border-accent/40 bg-accent/10 px-3 py-1 font-mono text-xs text-accent">
                      {isEn
                        ? 'API URL injected as VITE_API_URL'
                        : 'URL de API inyectada como VITE_API_URL'}
                    </span>
                  </div>
                </div>
              )}

              <div className="relative flex gap-4">
                {/* Vertical connector line */}
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-mono text-xs font-bold transition-colors ${
                      isHighlight
                        ? 'border-accent bg-accent/15 text-accent'
                        : 'border-border bg-bg-primary text-accent'
                    }`}
                  >
                    {step.number}
                  </div>
                  {!isLast && (
                    <div
                      className={`mt-1 w-px flex-1 ${isHighlight ? 'bg-accent/60' : 'bg-border'}`}
                      style={{ minHeight: '2rem' }}
                    />
                  )}
                </div>

                {/* Step content */}
                <div
                  className={`mb-2 flex-1 rounded border px-4 py-3 transition-colors ${
                    isHighlight
                      ? 'border-accent/40 bg-accent/5'
                      : 'border-border bg-bg-surface'
                  }`}
                >
                  <p
                    className={`text-sm font-semibold ${
                      isHighlight ? 'text-accent' : 'text-text-primary'
                    }`}
                  >
                    {isEn ? step.nameEn : step.nameEs}
                  </p>
                  <p className="mt-1 font-mono text-xs text-text-muted">
                    {isEn ? step.descEn : step.descEs}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
